import { useMemo } from 'react';

/**
 * Custom hook to transform and parse FillInTheBlank question data
 * Extracts complex logic from FillInTheBlankReview component
 */
export function useFillInTheBlankData(question) {
  return useMemo(() => {
    const blanks = [];
    const text = question['blank-passage'] || question.text || question['text-soal'] || '';
    
    // Parse [blank-1], [blank-2], etc.
    const blankMatches = text.match(/\[blank-\d+\]/g) || [];
    
    // Build choices map: idx -> text
    const choicesMap = {};
    if (question.choices && Array.isArray(question.choices)) {
      question.choices.forEach((choice, arrayIndex) => {
        const idx = choice.idx !== undefined ? choice.idx 
                 : choice.id !== undefined ? choice.id 
                 : arrayIndex;
        const choiceText = choice.text || choice['option-text'] || choice.option || '';
        if (idx !== undefined && idx !== null) {
          choicesMap[idx] = choiceText;
        }
      });
    }
    
    // Handle both quick practice (with choices) and writing practice (optionless-fib, no choices)
    if (blankMatches.length > 0) {
      const answers = question.answers || question['correct-answers'] || [];
      const hasChoices = question.choices && Array.isArray(question.choices) && question.choices.length > 0;
      
      blankMatches.forEach((placeholder, index) => {
        const blankNumberMatch = placeholder.match(/\[blank-(\d+)\]/);
        const blankNumber = blankNumberMatch ? parseInt(blankNumberMatch[1]) : index + 1;
        const blankId = blankNumber;
        
        if (hasChoices) {
          // Quick practice: answers are indices, need to map to choice text
        const correctIdx = Array.isArray(answers) ? answers[index] : null;
        const correctAnswerText = correctIdx !== null && choicesMap[correctIdx] 
          ? choicesMap[correctIdx] 
          : '';
        
        blanks.push({
          id: blankId,
          correctIdx: correctIdx,
          correctAnswer: correctAnswerText,
        });
        } else {
          // Writing practice (optionless-fib): answers are direct text strings
          const correctAnswerText = Array.isArray(answers) && answers[index] !== undefined
            ? String(answers[index])
            : '';
          
          blanks.push({
            id: blankId,
            correctIdx: null,
            correctAnswer: correctAnswerText,
          });
        }
      });
    }

    return {
      blanks,
      text,
      choicesMap,
    };
  }, [question]);
}

/**
 * Parse sentence with blanks into parts array
 */
export function parseSentenceWithBlanks(sentence, blanks) {
  if (!blanks || blanks.length === 0) return null;
  
  const parts = [];
  let currentIndex = 0;
  const blankMatches = [...sentence.matchAll(/\[blank-(\d+)\]/g)];
  
  blankMatches.forEach((match) => {
    const beforeBlank = sentence.substring(currentIndex, match.index);
    if (beforeBlank) {
      parts.push({ type: 'text', content: beforeBlank });
    }
    
    const blankNumber = parseInt(match[1]);
    const blank = blanks.find(b => b.id === blankNumber);
    if (blank) {
      parts.push({ type: 'blank', blank, blankIndex: blankNumber });
    }
    
    currentIndex = match.index + match[0].length;
  });
  
  const afterLastBlank = sentence.substring(currentIndex);
  if (afterLastBlank) {
    parts.push({ type: 'text', content: afterLastBlank });
  }
  
  return parts;
}

/**
 * Get choice text by idx
 */
export function getChoiceTextByIdx(choices, choicesMap, idx) {
  if (choices && Array.isArray(choices)) {
    const choice = choices.find(c => {
      const choiceIdx = c.idx !== undefined ? c.idx : c.id;
      return choiceIdx === idx || Number(choiceIdx) === Number(idx);
    });
    return choice?.text || choice?.['option-text'] || '';
  }
  return choicesMap[idx] || '';
}

/**
 * Parse userAnswer for FillInTheBlank into map by blankId
 */
export function useFillInTheBlankUserAnswers(userAnswer, blanks, choicesMap, getChoiceTextByIdxFn) {
  return useMemo(() => {
    const map = {};
    if (!blanks || blanks.length === 0) return map;
    
    const userAnswerIndices = Array.isArray(userAnswer) && userAnswer.length > 0 && typeof userAnswer[0] === 'number'
      ? userAnswer
      : [];
    
    // Handle array format: userAnswer is array of idx in order of blanks in text
    if (userAnswerIndices.length > 0) {
      blanks.forEach((blank, index) => {
        const blankId = blank.id;
        const userIdx = userAnswerIndices[index];
        
        if (userIdx !== undefined && userIdx !== null) {
          const userText = getChoiceTextByIdxFn(userIdx);
          map[blankId] = {
            idx: userIdx,
            text: userText || `[Unknown: ${userIdx}]`,
          };
        }
      });
    } else if (Array.isArray(userAnswer) && userAnswer.length > 0) {
      // Handle array of strings (for writing practice/optionless-fib) or array of text that might match choices
      const hasChoices = choicesMap && Object.keys(choicesMap).length > 0;
      
      blanks.forEach((blank, index) => {
        const blankId = blank.id;
        const userValue = userAnswer[index];
        
        if (userValue !== undefined && userValue !== null && userValue !== '') {
          const userText = String(userValue);
          
          if (hasChoices) {
            // Quick practice: try to find matching choice text in choicesMap
          let foundIdx = null;
          for (const [idx, text] of Object.entries(choicesMap)) {
            if (text === userText) {
              foundIdx = typeof idx === 'string' ? parseInt(idx, 10) : idx;
              break;
            }
          }
          
          map[blankId] = {
            idx: foundIdx !== null ? foundIdx : userText,
            text: userText,
          };
          } else {
            // Writing practice (optionless-fib): user answer is direct text, no choices to match
            map[blankId] = {
              idx: null,
              text: userText,
            };
          }
        }
      });
    } else if (userAnswer && typeof userAnswer === 'object') {
      // Handle object format: {1: 3, 2: 0}
      blanks.forEach((blank) => {
        const blankId = blank.id;
        const userIdx = userAnswer[blankId] !== undefined ? userAnswer[blankId] : userAnswer[String(blankId)];
        
        if (userIdx !== undefined && userIdx !== null && userIdx !== '') {
          const userText = choicesMap[userIdx];
          
          if (userIdx in choicesMap) {
            map[blankId] = {
              idx: userIdx,
              text: userText || '',
            };
          } else {
            map[blankId] = {
              idx: userIdx,
              text: String(userIdx),
            };
          }
        }
      });
    }
    
    return map;
  }, [userAnswer, blanks, choicesMap, getChoiceTextByIdxFn]);
}

